import { SensorType } from "@ui/utils/sensors";
/** NodeTypeCode of a relayed sensor of new hardware. */
export const RELAYED_SENSOR_TYPE_CODE = 3;
/** NodeTypeCode of the access point's own node. */
export const AP_NODE_TYPE_CODE = 100;
/** New hardware: relayed sensors and the AP's own node share the type-3 Edit Node layout and settings. */
export const isNewHardwareNodeType = (nodeTypeCode) => nodeTypeCode === RELAYED_SENSOR_TYPE_CODE ||
    nodeTypeCode === AP_NODE_TYPE_CODE;
const COLUMN_LIMIT = 999999;
/**
 * The sensors this client knows. The server decides which sensors a node has (one entry each in its settings), so a sensor
 * added there shows up here with generic texts until its names are added.
 */
const SENSOR_META = {
    [SensorType.InternalTemperature]: {
        labelKey: "common:labels.internalTemperature",
        titleKey: "common:headings.internalTemperatureCelsius",
        min: -COLUMN_LIMIT,
        max: COLUMN_LIMIT,
    },
    [SensorType.ExternalTemperature]: {
        labelKey: "common:labels.externalTemperature",
        titleKey: "common:headings.externalTemperatureCelsius",
        min: -COLUMN_LIMIT,
        max: COLUMN_LIMIT,
    },
    [SensorType.Humidity]: {
        labelKey: "common:labels.humidity",
        titleKey: "common:headings.humidityRh",
        min: 0,
        max: 100,
    },
    [SensorType.Co2]: {
        labelKey: "common:labels.co2",
        titleKey: "common:headings.co2Ppm",
        min: 0,
        max: COLUMN_LIMIT,
    },
};
const UNKNOWN_SENSOR_META = {
    labelKey: "common:alarmTypes.unknown",
    titleKey: "common:alarmTypes.unknown",
    min: -COLUMN_LIMIT,
    max: COLUMN_LIMIT,
};
export const sensorMeta = (sensorType) => { var _a; return (_a = SENSOR_META[sensorType]) !== null && _a !== void 0 ? _a : UNKNOWN_SENSOR_META; };
const DEFAULT_MEASUREMENT_INTERVAL = 120;
const DEFAULT_COMMUNICATION_INTERVAL = 40;
/** What the form shows before a node is loaded; the sensors come with the node. */
export const defaultNodeTypeThreeFormValues = () => ({
    measurementInterval: String(DEFAULT_MEASUREMENT_INTERVAL),
    communicationInterval: String(DEFAULT_COMMUNICATION_INTERVAL),
    temperatureName: "",
    sensors: [],
});
const text = (value) => value === null || value === undefined ? "" : String(value);
/** The two temperature sensors: a relayed sensor keeps at least one of them switched on. */
export const isTemperatureSensor = (sensorType) => sensorType === SensorType.InternalTemperature ||
    sensorType === SensorType.ExternalTemperature;
const toSensorFormValues = (sensor) => ({
    sensorType: sensor.sensorType,
    // As the server sends it: the AP node's internal temperature always comes back switched on.
    enabled: sensor.enabled,
    offset: text(sensor.offset),
    highAlarm: text(sensor.highAlarm),
    lowAlarm: text(sensor.lowAlarm),
});
export const toNodeTypeThreeFormValues = (config) => {
    var _a, _b;
    if (!config)
        return defaultNodeTypeThreeFormValues();
    return {
        measurementInterval: text(config.measurementIntervals),
        communicationInterval: text(config.communicationIntervals),
        temperatureName: (_a = config.temperatureName) !== null && _a !== void 0 ? _a : "",
        sensors: ((_b = config.sensors) !== null && _b !== void 0 ? _b : []).map(toSensorFormValues),
    };
};
/** An empty input is "not set" (null), never 0: a blank limit must not become an alarm at zero. */
const limit = (value) => {
    const trimmed = value.trim();
    if (trimmed === "")
        return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
};
const offset = (value) => {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : 0;
};
/**
 * The request block for a new-hardware node: the intervals and every sensor the form holds. A switched-off sensor has no
 * measurement card, so only its switch is saved: its offset and limits stay as they were loaded (`saved`).
 */
export const toNodeTypeThreeConfig = (values, saved) => {
    var _a;
    return ({
        measurementIntervals: Number(values.measurementInterval) || DEFAULT_MEASUREMENT_INTERVAL,
        communicationIntervals: Number(values.communicationInterval) || DEFAULT_COMMUNICATION_INTERVAL,
        temperatureName: ((_a = values.temperatureName) === null || _a === void 0 ? void 0 : _a.trim()) || null,
        sensors: values.sensors.map((current) => {
            var _a;
            const sensor = current.enabled
                ? current
                : Object.assign(Object.assign({}, ((_a = saved.sensors.find((s) => s.sensorType === current.sensorType)) !== null && _a !== void 0 ? _a : current)), { enabled: false });
            return {
                sensorType: sensor.sensorType,
                enabled: sensor.enabled,
                offset: offset(sensor.offset),
                highAlarm: limit(sensor.highAlarm),
                lowAlarm: limit(sensor.lowAlarm),
            };
        }),
    });
};
/** The form hands the rules whatever the field holds (inputs give strings, an unset field undefined). */
const asText = (value) => value === undefined || value === null ? "" : String(value);
const isNumber = (value) => value.trim() !== "" && Number.isFinite(Number(value.trim()));
/** react-hook-form rules of an interval input: a whole number of at least one second. */
export const intervalRules = (field) => ({
    validate: (value) => {
        const input = asText(value);
        return ((isNumber(input) && Number.isInteger(Number(input)) && Number(input) >= 1) ||
            `${field} must be a whole number of at least 1 second`);
    },
});
/** react-hook-form rules of a sensor's offset input: a number. */
export const offsetRules = () => ({
    validate: (value) => isNumber(asText(value)) || "Enter a number",
});
/**
 * Rules of a limit input of the sensor at \`index\`: optional, a number within the sensor's range, and the low limit below the
 * high one.
 */
export const limitRules = (index, side) => ({
    validate: (raw, form) => {
        var _a, _b, _c;
        const value = asText(raw);
        if (value.trim() === "")
            return true;
        if (!isNumber(value))
            return "Enter a number";
        const sensor = (_b = (_a = form.nodeTypeThree) === null || _a === void 0 ? void 0 : _a.sensors) === null || _b === void 0 ? void 0 : _b[index];
        const { min, max } = sensorMeta((_c = sensor === null || sensor === void 0 ? void 0 : sensor.sensorType) !== null && _c !== void 0 ? _c : 0);
        const parsed = Number(value);
        if (parsed < min || parsed > max)
            return `Must be between ${min} and ${max}`;
        const high = sensor ? limit(sensor.highAlarm) : null;
        const low = sensor ? limit(sensor.lowAlarm) : null;
        if (high !== null && low !== null && low >= high)
            return side === "lowAlarm"
                ? "Low alarm must be below the high alarm"
                : "High alarm must be above the low alarm";
        return true;
    },
});
